import React, { useEffect, useState, useCallback } from 'react';
import { Input, Select, Table, DropdownMenu, Spinner } from '@veda/dashboard-ui';
import { MdGroup, MdPersonAdd, MdPersonRemove, MdClose, MdMailOutline } from 'react-icons/md';
import { usePaApi } from '../services/paApi';
import { useAuthUser } from '../../ppp/hooks/useAuthUser';

const RUOLO_LABEL = {
  amministratore_ente:    'Amministratore ente',
  ufficio_tecnico:        'Ufficio tecnico',
  ufficio_amministrativo: 'Ufficio amministrativo',
  dirigente:              'Dirigente',
};

const RUOLO_OPTIONS = [
  { value: 'ufficio_tecnico',        label: 'Ufficio tecnico' },
  { value: 'ufficio_amministrativo', label: 'Ufficio amministrativo' },
  { value: 'dirigente',              label: 'Dirigente' },
  { value: 'amministratore_ente',    label: 'Amministratore ente' },
];

function Btn({ onClick, children, className = '', disabled = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  );
}

function Modale({ aperto, titolo, onChiudi, children }) {
  if (!aperto) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 backdrop-blur-sm p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onChiudi(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mt-16">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="font-semibold text-gray-800 dark:text-white">{titolo}</h2>
          <button onClick={onChiudi} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <MdClose size={20} />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function BadgeRuolo({ ruolo }) {
  return (
    <span className="inline-block px-2 py-0.5 text-xs font-medium rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
      {RUOLO_LABEL[ruolo] ?? ruolo}
    </span>
  );
}

function FormInvita({ onInvita, loading }) {
  const [email,  setEmail]  = useState('');
  const [ruolo,  setRuolo]  = useState('ufficio_tecnico');
  const [errore, setErrore] = useState('');

  const invia = () => {
    if (!email) return;
    setErrore('');
    onInvita({ email, ruolo }, (msg) => setErrore(msg));
  };

  return (
    <div className="space-y-4">
      <Input label="Email membro *" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
      <Select
        label="Ruolo *"
        value={ruolo}
        onChange={(e) => setRuolo(e?.target ? e.target.value : e)}
        options={RUOLO_OPTIONS}
      />
      {errore && <p className="text-sm text-red-600">{errore}</p>}
      <div className="flex justify-end">
        <button
          onClick={invia}
          disabled={loading || !email}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium transition-colors"
        >
          <MdMailOutline /> {loading ? 'Invio...' : 'Invia invito'}
        </button>
      </div>
    </div>
  );
}

export default function MembriEntePage() {
  const api = usePaApi();
  const { user, can } = useAuthUser();

  const [membri,         setMembri]         = useState([]);
  const [caricamento,    setCaricamento]    = useState(true);
  const [modaleInvita,   setModaleInvita]   = useState(false);
  const [invitoLoading,  setInvitoLoading]  = useState(false);
  const [msg,            setMsg]            = useState('');

  const carica = useCallback(() => {
    setCaricamento(true);
    api.listMembri({
      onSuccess: (data) => { setMembri(Array.isArray(data) ? data : []); setCaricamento(false); },
      onFormError: () => setCaricamento(false),
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const mostraMsg = (testo) => { setMsg(testo); setTimeout(() => setMsg(''), 3000); };

  const handleInvita = (data, onErr) => {
    setInvitoLoading(true);
    api.invitaMembro(data, {
      onSuccess: () => {
        setInvitoLoading(false);
        setModaleInvita(false);
        mostraMsg('Invito inviato con successo.');
        carica();
      },
      onFormError: (err) => {
        setInvitoLoading(false);
        const testo = err?.errors?.email?.[0] ?? err?.errors?.ruolo?.[0] ?? err?.message ?? 'Errore durante l\'invio.';
        onErr?.(testo);
      },
    });
  };

  const handleRimuovi = (userId) => {
    if (!window.confirm('Rimuovere questo membro? L\'account verrà sospeso.')) return;
    api.rimuoviMembro(userId, {
      onSuccess: () => { mostraMsg('Membro rimosso.'); carica(); },
      onFormError: (err) => mostraMsg(err?.message ?? 'Impossibile rimuovere il membro.'),
    });
  };

  const handleRevoca = (invitoId) => {
    if (!window.confirm('Revocare questo invito?')) return;
    api.revocaInvito(invitoId, {
      onSuccess: () => { mostraMsg('Invito revocato.'); carica(); },
      onFormError: (err) => mostraMsg(err?.message ?? 'Impossibile revocare l\'invito.'),
    });
  };

  const canInvita = can('ppp.membri.invite');
  const canRimuovi = can('ppp.membri.remove');

  if (caricamento && membri.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="w-full py-8 px-4 sm:px-6 lg:px-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center text-blue-600 dark:text-blue-400">
            <MdGroup size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">Membri ente</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Utenti e inviti del tuo ente</p>
          </div>
        </div>
        {canInvita && (
          <Btn onClick={() => setModaleInvita(true)} className="text-white bg-blue-600 hover:bg-blue-700">
            <MdPersonAdd /> Invita membro
          </Btn>
        )}
      </div>

      {msg && (
        <div className="mb-4 p-3 rounded-xl bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300 text-sm">
          {msg}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
        <Table
          cols={[
            { name: 'nome',   label: 'Nome' },
            { name: 'email',  label: 'Email' },
            { name: 'ruolo',  label: 'Ruolo', size: 'w-48' },
            { name: 'stato',  label: 'Stato', size: 'w-32' },
            { name: 'azioni', label: '',      size: 'w-10', orderable: 0 },
          ]}
          cells_style={{
            nome:  (row) => row.nome || <span className="text-gray-400">—</span>,
            ruolo: (row) => <BadgeRuolo ruolo={row.ruolo} />,
            stato: (row) => row.stato === 'invitato'
              ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">In attesa</span>
              : <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Attivo</span>,
            azioni: (row) => {
              if (!canRimuovi) return null;
              if (row.stato === 'invitato') {
                return (
                  <DropdownMenu
                    menu_items={[
                      <button key="revoca" onClick={() => handleRevoca(row.id)}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                        <MdClose size={16} /> Revoca invito
                      </button>,
                    ]}
                  />
                );
              }
              // Membro attivo: nascondi l'azione sulla riga dell'utente corrente.
              // user.id da auth-user è lo uuid; lo confrontiamo con row.uuid.
              if (user?.id && user.id === row.uuid) return null;
              return (
                <DropdownMenu
                  menu_items={[
                    <button key="rimuovi" onClick={() => handleRimuovi(row.id)}
                      className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600">
                      <MdPersonRemove size={16} /> Rimuovi membro
                    </button>,
                  ]}
                />
              );
            },
          }}
          dataset={membri}
        />
      </div>

      <Modale aperto={modaleInvita} titolo="Invita un nuovo membro" onChiudi={() => setModaleInvita(false)}>
        <FormInvita onInvita={handleInvita} loading={invitoLoading} />
      </Modale>
    </div>
  );
}
